import { useParams } from 'react-router';
import { useMatchByIdQuery } from '@data/matches/useMatchByIdQuery';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { useArticlesQuery } from '@data/articles/useArticlesQuery';
import { useLeagueTableQuery } from '@data/leagueTables/useLeagueTableQuery';
import { useIsMobile } from '../../utils/useIsMobile';
import { MatchSort, MatchStatusFilter } from '@repo/types';
import { MatchOverview } from '@ui/fixtures/components/MatchOverview';
import { Loader } from '@ui/common/components/Loader/Loader';
import { Helmet } from 'react-helmet-async';
import { NotFoundPage } from '../NotFoundPage';

// @ts-ignore fix vite import type
const isLeagueSite = import.meta.env.VITE_SITE_TYPE === 'league';

export const MatchPage = () => {
  const params = useParams();
  const isMobile = useIsMobile();

  const { data: match, isLoading: isMatchLoading, error: matchError } = useMatchByIdQuery(params.id);

  const { data: homeTeamArtices, isLoading: areHomeTeamArticlesLoading } = useArticlesQuery(
    {
      tags: isLeagueSite ? match?.homeTeam?.club?.slug : match?.homeTeam?.slug,
      limit: 3,
      endDate: match?.date,
    },
    {
      enabled: !!(isLeagueSite ? match?.homeTeam?.club?.slug : match?.homeTeam?.slug) && !!match?.date,
    }
  );

  const { data: homeTeamResults, isLoading: homeTeamResultsLoading } = useMatchesQuery(
    {
      limit: 3,
      sort: MatchSort.DESC,
      status: MatchStatusFilter.RESULT,
      teamId: match?.homeTeam?.id,
    },
    {
      enabled: !!match?.homeTeam?.id,
    }
  );

  const { data: homeTeamFixtures, isLoading: homeTeamFixturesLoading } = useMatchesQuery(
    {
      limit: 3,
      sort: MatchSort.DESC,
      status: MatchStatusFilter.FIXTURE,
      teamId: match?.homeTeam?.id,
    },
    {
      enabled: !!match?.homeTeam?.id,
    }
  );

  const { data: homeTeamLeagueTable, isLoading: isHomeTeamLeagueTableLoading } = useLeagueTableQuery(
    {
      teamId: match?.homeTeam?.id,
    },
    {
      enabled: !!match?.homeTeam?.id,
    }
  );

  const { data: awayTeamArticles, isLoading: areAwayTeamArticlesLoading } = useArticlesQuery(
    {
      tags: isLeagueSite ? match?.awayTeam?.club?.slug : match?.awayTeam?.slug,
      limit: 3,
      endDate: match?.date,
    },
    {
      enabled: !!(isLeagueSite ? match?.awayTeam?.club?.slug : match?.awayTeam?.slug) && !!match?.date,
    }
  );

  const { data: awayTeamResults, isLoading: awayTeamResultsLoading } = useMatchesQuery(
    {
      limit: 3,
      sort: MatchSort.DESC,
      status: MatchStatusFilter.RESULT,
      teamId: match?.awayTeam?.id,
    },
    {
      enabled: !!match?.awayTeam?.id,
    }
  );

  const { data: awayTeamFixtures, isLoading: awayTeamFixturesLoading } = useMatchesQuery(
    {
      limit: 3,
      sort: MatchSort.DESC,
      status: MatchStatusFilter.FIXTURE,
      teamId: match?.awayTeam?.id,
    },
    {
      enabled: !!match?.awayTeam?.id,
    }
  );

  const { data: awayTeamLeagueTable, isLoading: isAwayTeamLeagueTableLoading } = useLeagueTableQuery(
    {
      teamId: match?.awayTeam?.id,
    },
    {
      enabled: !!match?.awayTeam?.id,
    }
  );

  if (
    isMatchLoading ||
    areHomeTeamArticlesLoading ||
    homeTeamResultsLoading ||
    homeTeamFixturesLoading ||
    isHomeTeamLeagueTableLoading ||
    areAwayTeamArticlesLoading ||
    awayTeamResultsLoading ||
    awayTeamFixturesLoading ||
    isAwayTeamLeagueTableLoading
  ) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!match || matchError) {
    return <NotFoundPage />;
  }

  const metaTitle = `${match.homeTeam.fullName} v ${match.awayTeam.fullName}, 2025/26 | ${
    import.meta.env.VITE_SITE_NAME
  }`;
  const metaDescription = `Read about ${match.homeTeam.fullName} v ${match.awayTeam.fullName} in the ${
    import.meta.env.VITE_ENTITY_NAME
  } 2025/26 season, including lineups, stats and live blogs, on the official website of the ${
    import.meta.env.VITE_SITE_NAME
  }.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <MatchOverview
        isMobile={isMobile}
        match={match}
        homeTeamArticles={homeTeamArtices}
        homeTeamResults={homeTeamResults}
        homeTeamFixtures={homeTeamFixtures}
        homeTeamLeagueTable={homeTeamLeagueTable}
        awayTeamArticles={awayTeamArticles}
        awayTeamResults={awayTeamResults}
        awayTeamFixtures={awayTeamFixtures}
        awayTeamLeagueTable={awayTeamLeagueTable}
      />
    </>
  );
};
